/**
 * G13 / V34 in the browser: per-project changelogs and digests (decrypted from the machines'
 * encrypted hygiene reports), the per-user "last visited" marker (`visit` entity, synced across
 * trusted devices; the newest visit wins) and the digest settings (`setting` entity).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type ChangelogItem,
  type ChangelogReport,
  DEFAULT_DIGEST_SETTINGS,
  type DigestReport,
  DigestSettings,
  type EvidenceRef,
  itemsSince,
  LastVisitEntry,
  lastVisitEntityId,
  mergeVisit,
  SettingEntry,
  settingEntityId,
} from '@viko/protocol'
import { useBackend } from './backend.js'
import type { BrowserEntities } from './encrypted-entities.js'
import { activePoll } from './query-client.js'
import type { InboxItem } from './types.js'

type Entities = Pick<BrowserEntities, 'list' | 'write'>

/** One changelog per project: items from every machine, deduplicated by id (newest wins). */
export function mergeChangelogs(reports: readonly ChangelogReport[]) {
  const out = new Map<string, { projectName?: string; items: Map<string, ChangelogItem> }>()
  for (const r of reports) {
    const p = out.get(r.projectId) ?? { items: new Map<string, ChangelogItem>() }
    if (r.projectName) p.projectName = r.projectName
    for (const i of r.items) {
      const was = p.items.get(i.id)
      if (!was || i.at > was.at || (i.at === was.at && i.count > was.count)) p.items.set(i.id, i)
    }
    out.set(r.projectId, p)
  }
  return new Map(
    [...out].map(([id, p]) => [
      id,
      {
        ...(p.projectName ? { projectName: p.projectName } : {}),
        items: [...p.items.values()].sort((a, b) => b.at - a.at || a.id.localeCompare(b.id)),
      },
    ]),
  )
}

// ---- last visited ------------------------------------------------------------------------------

export async function loadVisits(entities: Entities): Promise<Map<string, number>> {
  const out = new Map<string, number>()
  for (const row of await entities.list('visit')) {
    const v = LastVisitEntry.safeParse(row.value)
    // The id is derived from the project id; a value naming another project is not trusted.
    if (!v.success || lastVisitEntityId(v.data.projectId) !== row.id) continue
    out.set(v.data.projectId, Math.max(out.get(v.data.projectId) ?? 0, v.data.visitedAt))
  }
  return out
}

/**
 * Move the marker forward (never back): a device with a stale clock or an older view cannot
 * un-see what another device already saw. Returns the marker now stored.
 */
export async function markVisited(
  entities: Entities,
  projectId: string,
  at: number,
  known?: number,
): Promise<number> {
  const next = mergeVisit(known !== undefined ? { projectId, visitedAt: known } : undefined, {
    projectId,
    visitedAt: at,
  })
  if (known !== undefined && next.visitedAt === known) return known
  await entities.write('visit', lastVisitEntityId(projectId), LastVisitEntry.parse(next))
  return next.visitedAt
}

// ---- visit sessions (D3) -----------------------------------------------------------------------

/** Time in a project before leaving counts as a visit. */
export const VISIT_DWELL_MS = 10_000
/** Coming back within this long of leaving continues the same visit (same baseline). */
export const VISIT_GRACE_MS = 15 * 60_000

interface VisitSession {
  baseline: number | undefined
  openedAt: number
  leftAt?: number
}

/**
 * The since-visit baseline is fixed for a visit. The stored marker moves when the user leaves a
 * project they viewed for {@link VISIT_DWELL_MS}, not when they open it, and a return within
 * {@link VISIT_GRACE_MS} keeps the previous baseline so tab-switching never reads "just now".
 */
export class VisitTracker {
  private sessions = new Map<string, VisitSession>()

  /** The project came into view; returns the baseline to diff against. */
  open(projectId: string, marker: number | undefined, now: number): number | undefined {
    const s = this.sessions.get(projectId)
    if (s && (s.leftAt === undefined || now - s.leftAt <= VISIT_GRACE_MS)) {
      delete s.leftAt
      return s.baseline
    }
    this.sessions.set(projectId, { baseline: marker, openedAt: now })
    return marker
  }

  /** The project left view; returns the time to store as the new marker, if the dwell was long enough. */
  leave(projectId: string, now: number): number | undefined {
    const s = this.sessions.get(projectId)
    if (!s || s.leftAt !== undefined) return undefined
    s.leftAt = now
    return now - s.openedAt >= VISIT_DWELL_MS ? now : undefined
  }

  /** Baseline reset without leaving ("Mark as seen"). */
  reset(projectId: string, baseline: number) {
    const s = this.sessions.get(projectId)
    if (s) s.baseline = baseline
  }
}

/** One tracker per browser tab: survives project tab switches (component unmounts). */
export const visitTracker = new VisitTracker()

/**
 * The page head's since-visit line: counts when something changed; "Nothing changed since …"
 * once the last look is at least the grace period old; hidden otherwise (a quick tab switch, or a
 * first visit with nothing to report).
 */
export function sinceLineState(o: {
  at: number | undefined
  now: number
  changes: number
}): 'counts' | 'quiet' | 'hidden' {
  if (o.changes > 0) return 'counts'
  if (o.at === undefined || o.now - o.at < VISIT_GRACE_MS) return 'hidden'
  return 'quiet'
}

/** Digest counts by type; each opens History › Changes filtered to that kind. */
export const DIGEST_COUNT_TYPES = [
  { key: 'landed', label: 'landed', kind: 'landed' },
  { key: 'failed', label: 'failed', kind: 'failure' },
  { key: 'decided', label: 'decided', kind: 'decision' },
] as const
export function digestCounts(items: readonly Pick<ChangelogItem, 'kind'>[]) {
  return DIGEST_COUNT_TYPES.map((t) => ({
    ...t,
    n: items.filter((i) => i.kind === t.kind).length,
  })).filter((c) => c.n > 0)
}

// ---- settings ----------------------------------------------------------------------------------

export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export async function loadDigestSettings(entities: Entities): Promise<DigestSettings | undefined> {
  const id = settingEntityId('digests')
  const row = (await entities.list('setting')).find((r) => r.id === id)
  const entry = SettingEntry.safeParse(row?.value)
  if (!entry.success || entry.data.name !== 'digests') return undefined
  const s = DigestSettings.safeParse(entry.data.value)
  return s.success ? s.data : undefined
}

export async function saveDigestSettings(entities: Entities, s: DigestSettings, now = Date.now()) {
  const value = DigestSettings.parse({ ...s, updatedAt: now })
  await entities.write(
    'setting',
    settingEntityId('digests'),
    SettingEntry.parse({ name: 'digests', value, updatedAt: now }),
  )
  return value
}

export const defaultSettings = (): DigestSettings => ({
  ...DEFAULT_DIGEST_SETTINGS,
  timezone: browserZone(),
})

// ---- digests → inbox cards ---------------------------------------------------------------------

const CARD_HOURS = 18
export const digestCardId = (d: Pick<DigestReport, 'slot' | 'to' | 'scope'>) =>
  `d:${d.slot}:${d.to}${d.scope === 'all' ? '' : `:${d.scope.projectId.replace(/[^A-Za-z0-9_.:-]/g, '_').slice(0, 80)}`}`

/** The newest digest per scope + slot (every machine publishes its own; newest wins). */
export function latestDigests(reports: readonly DigestReport[]): DigestReport[] {
  const out = new Map<string, DigestReport>()
  for (const d of reports) {
    const key = `${d.scope === 'all' ? 'all' : d.scope.projectId}:${d.slot}`
    const was = out.get(key)
    if (!was || d.generatedAt > was.generatedAt) out.set(key, d)
  }
  return [...out.values()].sort((a, b) => b.generatedAt - a.generatedAt)
}

/** Recent digests become one FYI card each (snoozable by opaque id; never blocking). */
export function digestCardItems(
  reports: readonly DigestReport[],
  snoozes: Record<string, number>,
  now: number,
): InboxItem[] {
  return latestDigests(reports).flatMap((d) => {
    const id = digestCardId(d)
    if (now - d.generatedAt > CARD_HOURS * 3_600_000 || (snoozes[id] ?? 0) > now) return []
    const name = d.scope === 'all' ? 'All projects' : (d.sections[0]?.projectName ?? 'Project')
    return [
      {
        id,
        itemId: id,
        runId: '',
        type: 'card' as const,
        kind: 'fyi' as const,
        category: 'digest',
        seq: 0,
        title: d.headline,
        blocking: false,
        createdAt: d.generatedAt,
        score: 0,
        projectName: name,
        body: d,
        derived: true,
      },
    ]
  })
}

// ---- drill-down --------------------------------------------------------------------------------

export type EvidenceTarget =
  | { kind: 'route'; to: string; params?: Record<string, string>; search?: Record<string, string> }
  | { kind: 'url'; url: string }

/** Where a piece of evidence opens (every indicator is drillable). */
export function evidenceTarget(
  e: EvidenceRef,
  item: Pick<ChangelogItem, 'projectId' | 'runId' | 'workstreamId'>,
): EvidenceTarget {
  if (e.url) return { kind: 'url', url: e.url }
  const project = {
    kind: 'route' as const,
    to: '/projects/$projectId',
    params: { projectId: item.projectId },
  }
  switch (e.kind) {
    case 'run':
      return { kind: 'route', to: '/s/$runId', params: { runId: e.id } }
    case 'question':
    case 'blocker':
      return item.runId
        ? { kind: 'route', to: '/s/$runId', params: { runId: item.runId } }
        : { ...project, search: { tab: 'changes' } }
    case 'workstream':
      return { kind: 'route', to: '/w/$workstreamId', params: { workstreamId: e.id } }
    case 'task':
      return { kind: 'route', to: '/t/$taskId', params: { taskId: e.id } }
    case 'build':
    case 'lane':
      return { kind: 'route', to: '/releases' }
    case 'landing':
    case 'commit':
      return item.workstreamId
        ? { kind: 'route', to: '/w/$workstreamId', params: { workstreamId: item.workstreamId } }
        : { ...project, search: { tab: 'landing' } }
    default:
      return { ...project, search: { tab: 'changes' } }
  }
}

// ---- hooks -------------------------------------------------------------------------------------

export function useChangelogs() {
  const b = useBackend()
  return useQuery({
    queryKey: ['changelogs', b.artifactAccess.accountId] as const,
    queryFn: async () => mergeChangelogs(await b.api.changelogs()),
    retry: false,
    // Hygiene reports have no hub push; they change a few times a day.
    ...activePoll(5 * 60_000),
  })
}

export function useDigests() {
  const b = useBackend()
  return useQuery({
    queryKey: ['digests', b.artifactAccess.accountId] as const,
    queryFn: async () => latestDigests(await b.api.digests()),
    retry: false,
    ...activePoll(5 * 60_000),
  })
}

export function useLastVisits() {
  const b = useBackend()
  const entities = b.entities
  return useQuery({
    queryKey: ['visits', b.artifactAccess.accountId] as const,
    queryFn: async () => (entities ? loadVisits(entities) : new Map<string, number>()),
    enabled: !!entities,
    retry: false,
    // This tab's own visits update the cache directly; other devices' arrive within 5 min.
    ...activePoll(5 * 60_000),
  })
}

export function useMarkVisited() {
  const b = useBackend()
  const qc = useQueryClient()
  return async (projectId: string, known?: number, at = Date.now()) => {
    const entities = b.entities
    if (!entities) return
    const stored = await markVisited(entities, projectId, at, known)
    qc.setQueryData(
      ['visits', b.artifactAccess.accountId],
      (old: Map<string, number> | undefined) => new Map(old ?? []).set(projectId, stored),
    )
  }
}

export function useDigestSettings() {
  const b = useBackend()
  const entities = b.entities
  return useQuery({
    queryKey: ['digest-settings', b.artifactAccess.accountId] as const,
    queryFn: async () => (entities ? ((await loadDigestSettings(entities)) ?? null) : null),
    enabled: !!entities,
    retry: false,
  })
}

/** Count of items since the user's marker, per project (badges). */
export function sinceCounts(
  changelogs: ReadonlyMap<string, { items: ChangelogItem[] }> | undefined,
  visits: ReadonlyMap<string, number> | undefined,
): Map<string, number> {
  const out = new Map<string, number>()
  if (!changelogs || !visits) return out
  for (const [projectId, c] of changelogs) {
    const n = itemsSince(c.items, visits.get(projectId)).length
    if (n) out.set(projectId, n)
  }
  return out
}
